import { Link } from "react-router-dom";
import { MessagesSquare, Brain, MapPin, Compass, ArrowUpRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { Stagger, StaggerItem } from "@/components/Reveal";

const CARDS = [
  {
    n: "01",
    icon: MessagesSquare,
    title: "Psicoterapia",
    text: "Um espaço profissional de escuta e compreensão para questões emocionais, comportamentais e relacionais, respeitando a história de cada pessoa.",
    items: ["Adultos", "Adolescentes", "Crianças"],
    to: "/atendimento/psicoterapia-online",
    cta: "Conhecer o atendimento",
    testId: "ajudar-card-psicoterapia",
  },
  {
    n: "02",
    icon: Brain,
    title: "Avaliação Neuropsicológica",
    text: "Processo clínico estruturado que busca compreender o funcionamento cognitivo, emocional e comportamental, considerando o contexto de cada pessoa.",
    items: ["Atenção", "Memória", "Funções executivas", "Aprendizagem"],
    to: "/atendimento/avaliacao-neuropsicologica",
    cta: "Conhecer a avaliação",
    testId: "ajudar-card-avaliacao",
  },
  {
    n: "03",
    icon: MapPin,
    title: "Psicoterapia Presencial",
    text: "Em Taboão da Serra — SP, no Metropolitan Office, ao lado do Shopping Taboão. Também realizo atendimento online, por videochamada.",
    items: ["Presencial", "Online"],
    to: "/atendimento/atendimento-presencial",
    cta: "Conhecer o atendimento",
    testId: "ajudar-card-presencial",
  },
  {
    n: "04",
    icon: Compass,
    title: "Outras possibilidades",
    text: "Reavaliação neuropsicológica, orientação a pais e organização de rotina para adultos com TDAH. Se a sua demanda for outra, converse comigo.",
    items: ["Reavaliação", "Orientação a pais", "Rotina e TDAH adulto"],
    to: "/atendimento/outras-possibilidades",
    cta: "Conhecer as possibilidades",
    testId: "ajudar-card-outras",
  },
];

const cardCls =
  "group relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-raiz-900/10 bg-cream p-8 transition-[transform,border-color,background-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-gold-400/60 hover:bg-paper hover:shadow-xl hover:shadow-raiz-900/10 lg:p-10";

const CardInner = ({ c }) => (
  <>
    <span
      aria-hidden="true"
      className="absolute left-8 right-8 top-0 h-px origin-left scale-x-0 bg-gold-500 transition-transform duration-700 ease-out group-hover:scale-x-100 lg:left-10 lg:right-10"
    />
    <div className="flex items-start justify-between">
      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-raiz-900/10 bg-paper text-raiz-700 transition-colors duration-500 group-hover:border-gold-400/60 group-hover:text-gold-600">
        <c.icon aria-hidden="true" className="h-6 w-6 transition-transform duration-500 group-hover:-translate-y-0.5" />
      </span>
      <span aria-hidden="true" className="font-serif text-4xl font-light text-raiz-200 transition-colors duration-500 group-hover:text-gold-400">
        {c.n}
      </span>
    </div>
    <h3 className="mt-8 font-serif text-2xl font-light text-ink lg:text-3xl">{c.title}</h3>
    <p className="mt-4 text-sm leading-relaxed text-ink/65">{c.text}</p>
    <ul className="mt-6 flex flex-wrap gap-2">
      {c.items.map((i) => (
        <li key={i} className="rounded-full border border-raiz-800/15 px-3.5 py-1.5 text-xs text-raiz-800">
          {i}
        </li>
      ))}
    </ul>
    <span className="mt-auto inline-flex items-center gap-2 pt-8 text-sm font-medium text-raiz-800 transition-colors duration-300 group-hover:text-gold-700">
      {c.cta}
      <ArrowUpRight aria-hidden="true" className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
    </span>
  </>
);

const ComoAjudar = () => (
  <section data-testid="como-ajudar-section" className="border-t border-raiz-900/10 py-20 lg:py-28">
    <div className="container-x">
      <SectionHeading
        eyebrow="02 — Atendimentos"
        title={
          <>
            Como podemos <em className="italic text-raiz-700">ajudar?</em>
          </>
        }
        lead="Cada pessoa chega com uma história. Estes são os caminhos de cuidado disponíveis — e o primeiro contato ajuda a compreender qual deles faz sentido para você."
        testId="como-ajudar"
      />
      <Stagger className="mt-14 grid gap-6 md:grid-cols-2" gap={0.1}>
        {CARDS.map((c) => (
          <StaggerItem key={c.n} className="h-full">
            <Link to={c.to} data-testid={c.testId} className={cardCls}>
              <CardInner c={c} />
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

export default ComoAjudar;
